import { useState, useEffect, useCallback } from "react";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  organizationName: string | null;
  organizationId: string | null;
  active : boolean;
}

export interface Organization {
  id: string;
  name: string;
  default_language?: string | null;
  settings?: Record<string, any> | null;
  email_sending_enabled?: boolean | null;
  free_trial_days?: number | null;
  payment_grace_days?: number | null;
  skip_subscription_check?: boolean | null;
  subscription_issue?: string | null;
  total_nbr_cv?: number | null;
  total_nrb_job_offer?: number | null;
}

export const useAdminData = () => {
  const { isSuperAdmin, isAdmin} = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    
    // If not a super admin, don't fetch data
    if (!isSuperAdmin && !isAdmin) {
      console.warn("User is not admin, not fetching data");
      setIsLoading(false);
      return;
    }
      try {
      //console.log("Fetching admin data...");
      
      // Fetch organizations first to create mapping
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('id, name, default_language, settings, email_sending_enabled');

      if (orgError) {
        console.error("Error fetching Organizations:", orgError);
        throw orgError;
      }

      // Fetch subscription settings for each org
      const { data: subscriptionData } = await supabase
        .from('organization_subscription')
        .select('id, free_trial_days, payment_grace_days, skip_subscription_check, subscription_issue, total_nbr_cv, total_nrb_job_offer');

      const subscriptionMap = new Map<string, any>(
        subscriptionData?.map((s: any) => [s.id, s]) ?? []
      );

      const orgDataWithTrials = (orgData || []).map((org: Organization) => {
        const sub = subscriptionMap.get(org.id);
        return {
          ...org,
          free_trial_days: sub?.free_trial_days ?? null,
          payment_grace_days: sub?.payment_grace_days ?? null,
          skip_subscription_check: sub?.skip_subscription_check ?? null,
          subscription_issue: sub?.subscription_issue ?? null,
          total_nbr_cv: sub?.total_nbr_cv ?? null,
          total_nrb_job_offer: sub?.total_nrb_job_offer ?? null,
        };
      });

      //console.log("Fetched organization:", orgData);
      setOrganizations(orgDataWithTrials);

      const { data: organizationUser, error: organizationUserError } = await supabase
        .from('user_organization')
        .select('organization_uuid, user_uuid');
        
      if (organizationUserError) {
        console.error("Error fetching user_organization:", organizationUserError);
        throw organizationUserError;
      }
      
      //console.log("Fetched user_organization data:", organizationUser);
      
      // Create a map of company IDs to names for quick lookup
      const companyMap = new Map<string, string>();
      if (orgData) {
        orgData.forEach((company: Organization) => {
          companyMap.set(company.id, company.name);
        });
      }
      
      // Create a map of user IDs to organization IDs
      const userOrgMap = new Map<string, string>();
      if (organizationUser) {
        organizationUser.forEach((relation: { user_uuid: string; organization_uuid: string }) => {
          userOrgMap.set(relation.user_uuid, relation.organization_uuid);
        });
      }

      // Fetch all profiles - this is our main source of user data
      //console.log("Fetching all user profiles...");
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, email, first_name, last_name, role, active');

      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
        throw profilesError;
      }

      //console.log("Raw profiles data:", profilesData);
      //console.log("Number of profiles fetched:", profilesData?.length || 0);
      
      // Transform profiles data with proper mapping from user_organization
      const formattedUsers: User[] = profilesData ? profilesData.map(profile => {
        // Look up the organization ID for this user
        const orgId = userOrgMap.get(profile.id) || null;
        
        return {
          id: profile.id,
          email: profile.email || '',
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          role: profile.role || 'user',
          organizationId: orgId,
          organizationName: orgId && companyMap.has(orgId) 
            ? companyMap.get(orgId)! 
            : null,
          active: profile.active || false,
        };
      }) : [];
      
      //console.log("Formatted users:", formattedUsers);
      //console.log("Number of formatted users:", formattedUsers.length);
      
      setUsers(formattedUsers);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast({
        title: "Error",
        description: "Failed to load users and organizations",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    // Only fetch data if the user is a super admin
    if (isSuperAdmin) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, [fetchData, isSuperAdmin]);

  return {
    users,
    organizations,
    isLoading,
    fetchData
  };
};
